import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	Avatar,
  } from '@mui/material';

import { Visibility, VisibilityOff } from '@mui/icons-material';

import { makeStyles } from "@mui/styles";
import { green } from "@mui/material/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	avatarWrapper: {
		display: "flex",
		justifyContent: "center",
		marginBottom: theme.spacing(2),
	},
	avatarClickable: {
		cursor: "pointer",
		width: 72,
		height: 72,
		fontSize: 28,
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, i18n.t("userModal.validation.nameShort"))
		.max(50, i18n.t("userModal.validation.nameLong"))
		.required(i18n.t("userModal.validation.required")),
	password: Yup.string()
		// Campo vazio ("nova senha" não preenchida) precisa ser tratado como
		// "não informado" — sem o transform, Yup roda .min(5) contra a string
		// vazia (0 < 8) e barra o salvamento mesmo quando só o nome mudou.
		.transform(value => (value ? value : undefined))
		// Regra espelhada do backend (UpdateUserService/CreateUserService): sem
		// isso o formulário aceita senhas que a API rejeita depois, e o usuário
		// só descobre no toast de erro.
		.min(8, "A senha deve ter no mínimo 8 caracteres")
		.matches(/(?=.*[a-zA-Z])(?=.*\d)/, "A senha deve conter pelo menos uma letra e um número")
		.max(50, i18n.t("userModal.validation.passwordLong")),
	email: Yup.string()
		.email(i18n.t("userModal.validation.emailInvalid"))
		.required(i18n.t("userModal.validation.required")),
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		currentPassword: "",
		profile: "user"
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappId, setWhatsappId] = useState(false);
	const [profileImage, setProfileImage] = useState(null);
	const {loading, whatsApps} = useContext(WhatsAppsContext);

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => {
					return { ...prevState, ...data };
				});
				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : '');
				setProfileImage(data.profileImage || null);
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
	};

	const handleAvatarChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = (ev) => {
			const img = new Image();
			img.onload = () => {
				const MAX = 256;
				const scale = Math.min(MAX / img.width, MAX / img.height, 1);
				const canvas = document.createElement("canvas");
				canvas.width = Math.round(img.width * scale);
				canvas.height = Math.round(img.height * scale);
				canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
				setProfileImage(canvas.toDataURL("image/jpeg", 0.8));
			};
			img.src = ev.target.result;
		};
		reader.readAsDataURL(file);
	};

	const handleSaveUser = async values => {
		const userData = { ...values, whatsappId, queueIds: selectedQueueIds, profileImage };
		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.avatarWrapper}>
									<input
										type="file"
										accept="image/*"
										id="avatar-upload"
										style={{ display: "none" }}
										onChange={handleAvatarChange}
									/>
									<label htmlFor="avatar-upload">
										<Avatar
											src={profileImage || undefined}
											className={classes.avatarClickable}
										>
											{!profileImage && user.name ? user.name[0].toUpperCase() : null}
										</Avatar>
									</label>
								</div>
								<Field
									as={TextField}
									label={i18n.t("userModal.form.name")}
									autoFocus
									name="name"
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									fullWidth
								/>
								<Field
									as={TextField}
									label={i18n.t("userModal.form.email")}
									name="email"
									error={touched.email && Boolean(errors.email)}
									helperText={touched.email && errors.email}
									variant="outlined"
									margin="dense"
									fullWidth
								/>
								<Field
									as={TextField}
									name="password"
									variant="outlined"
									margin="dense"
									label={userId ? "Nova senha (deixe vazio para manter)" : i18n.t("userModal.form.password")}
									error={touched.password && Boolean(errors.password)}
									helperText={touched.password && errors.password}
									type={showPassword ? 'text' : 'password'}
									InputProps={{
									endAdornment: (
										<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={() => setShowPassword((e) => !e)}
										>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
										</InputAdornment>
									)
									}}
									fullWidth
								/>
								{/* Confirmação com senha atual — só quando o usuário edita a
								    PRÓPRIA senha. Admin trocando a senha de outro não sabe (nem
								    precisa saber) a senha atual dele. */}
								{userId && values.password &&
									String(loggedInUser.id) === String(userId) && (
									<Field
										as={TextField}
										name="currentPassword"
										variant="outlined"
										margin="dense"
										label="Senha atual (confirmação)"
										type="password"
										fullWidth
									/>
								)}
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<FormControl
											variant="outlined"
											margin="dense"
											fullWidth
										>
											<InputLabel id="profile-selection-input-label">
												{i18n.t("userModal.form.profile")}
											</InputLabel>

											<Field
												as={Select}
												label={i18n.t("userModal.form.profile")}
												name="profile"
												labelId="profile-selection-label"
												id="profile-selection"
												required
											>
												<MenuItem value="admin">Admin</MenuItem>
												<MenuItem value="supervisor">Supervisor</MenuItem>
												<MenuItem value="user">Atendente</MenuItem>
												<MenuItem value="arte_finalista">Arte Finalista</MenuItem>
												<MenuItem value="financeiro">Financeiro</MenuItem>
											</Field>
										</FormControl>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<QueueSelect
											selectedQueueIds={selectedQueueIds}
											onChange={values => setSelectedQueueIds(values)}
										/>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (!loading &&
										<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
											<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
											<Field
												as={Select}
												value={whatsappId}
												onChange={(e) => setWhatsappId(e.target.value)}
												label={i18n.t("userModal.form.whatsapp")}
											>
												<MenuItem value={''}>&nbsp;</MenuItem>
												{whatsApps.map((whatsapp) => (
													<MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
												))}
											</Field>
										</FormControl>
									)}
								/>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
